import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { FailureToast, SuccessToast } from 'components/Toast';
import {
  fetchContentGridPersonalizationSets,
  resetContentGridPersonalization,
  saveContentGridPersonalizationSets,
} from 'actions/contentGridPersonalization';
import {
  apiSetsToDrafts,
  draftsToApiSets,
  pruneSetAssetIds,
} from './personalizationMappers';
import {
  personalizationSetsReadyToSave,
  setHasCompleteAudienceFilters,
} from './personalizationFilterUtils';

const toastSuccess = (description) =>
  toast(<SuccessToast heading="Success" description={description} />, {
    position: toast.POSITION.BOTTOM_RIGHT,
    autoClose: 3000,
  });

const toastFailure = (description) =>
  toast(<FailureToast heading="Failure" description={description} />, {
    position: toast.POSITION.BOTTOM_RIGHT,
    autoClose: 3000,
  });

const personalizationStatePath = (editorSession) =>
  `${editorSession}.present.layoutManager.contentGridPersonalization`;

export const useContentGridPersonalizationEditor = ({
  editorSession,
  layoutId,
  storyboardId,
  dynamic,
  enabled,
}) => {
  const dispatch = useDispatch();
  const [sets, setSets] = useState([]);
  const [activeSetId, setActiveSetId] = useState();
  const [dirty, setDirty] = useState(false);

  const loading = useSelector((state) =>
    _.get(state, `${personalizationStatePath(editorSession)}.loading`, false),
  );
  const saving = useSelector((state) =>
    _.get(state, `${personalizationStatePath(editorSession)}.saving`, false),
  );
  const fetchedSets = useSelector((state) =>
    _.get(state, `${personalizationStatePath(editorSession)}.sets`, []),
  );
  const fetchedLayoutId = useSelector((state) =>
    _.get(state, `${personalizationStatePath(editorSession)}.layoutId`, null),
  );
  const fetchError = useSelector((state) =>
    _.get(state, `${personalizationStatePath(editorSession)}.error`, null),
  );

  useEffect(() => {
    if (!enabled || !layoutId || !editorSession) return;
    dispatch(fetchContentGridPersonalizationSets(editorSession, layoutId));
  }, [dispatch, enabled, layoutId, editorSession]);

  useEffect(() => {
    if (fetchError && enabled) {
      toastFailure('Failed to load personalization sets');
    }
  }, [fetchError, enabled]);

  useEffect(() => {
    if (!enabled || fetchedLayoutId !== layoutId) return;
    const drafts = apiSetsToDrafts(fetchedSets);
    setSets(drafts);
    setActiveSetId(drafts[0]?.clientId);
    setDirty(false);
  }, [enabled, fetchedSets, fetchedLayoutId, layoutId]);

  useEffect(() => {
    if (!enabled && editorSession) {
      dispatch(resetContentGridPersonalization(editorSession));
    }
  }, [enabled, editorSession, dispatch]);

  const onSetsChange = useCallback((next) => {
    setSets(next);
    setDirty(true);
  }, []);

  const persistSets = useCallback(
    (nextSets) =>
      new Promise((resolve) => {
        if (!layoutId || !editorSession) {
          resolve(false);
          return;
        }
        dispatch(
          saveContentGridPersonalizationSets(
            editorSession,
            layoutId,
            storyboardId,
            draftsToApiSets(nextSets, dynamic),
            resolve,
          ),
        );
      }),
    [dispatch, dynamic, editorSession, layoutId, storyboardId],
  );

  const discardChanges = useCallback(() => {
    if (!layoutId || fetchedLayoutId !== layoutId) {
      setSets([]);
      setActiveSetId(undefined);
      setDirty(false);
      return;
    }
    const drafts = apiSetsToDrafts(fetchedSets);
    setSets(drafts);
    setActiveSetId(drafts[0]?.clientId);
    setDirty(false);
  }, [fetchedLayoutId, fetchedSets, layoutId]);

  const save = useCallback(async () => {
    if (!layoutId) return false;
    if (!personalizationSetsReadyToSave(sets, dynamic)) {
      toastFailure(
        'Complete each personalization set (name, filters, and assets) before saving',
      );
      return false;
    }
    const ok = await persistSets(sets);
    if (ok) {
      setDirty(false);
      toastSuccess('Personalization saved');
    } else {
      toastFailure('Failed to save personalization');
    }
    return ok;
  }, [dynamic, layoutId, persistSets, sets]);

  const activeSet = sets.find((s) => s.clientId === activeSetId);
  const canSelectAssets = setHasCompleteAudienceFilters(activeSet);

  const toggleAssetForActiveSet = useCallback(
    (mappingId, selected) => {
      if (!setHasCompleteAudienceFilters(sets.find((s) => s.clientId === activeSetId))) {
        return;
      }
      setSets((prev) =>
        prev.map((set) => {
          if (set.clientId !== activeSetId) return set;
          const key = dynamic ? 'contentMappingIds' : 'layoutMappingIds';
          const ids = new Set(set[key]);
          if (selected) ids.add(mappingId);
          else ids.delete(mappingId);
          return { ...set, [key]: Array.from(ids) };
        }),
      );
      setDirty(true);
    },
    [activeSetId, dynamic, sets],
  );

  const pruneToAllowedIdsAndSave = useCallback(
    async (allowedIds) => {
      if (!layoutId) return false;
      const allowed = new Set(allowedIds);
      let nextSets = [];
      let changed = false;
      setSets((prev) => {
        nextSets = pruneSetAssetIds(prev, allowed, dynamic);
        changed = JSON.stringify(prev) !== JSON.stringify(nextSets);
        return changed ? nextSets : prev;
      });
      if (!changed) return false;

      if (!personalizationSetsReadyToSave(nextSets, dynamic)) {
        toastFailure(
          'Complete each personalization set (name, filters, and assets) before saving',
        );
        return false;
      }

      const ok = await persistSets(nextSets);
      if (ok) {
        setDirty(false);
      } else {
        setDirty(true);
        toastFailure('Failed to update personalization after filter change');
      }
      return ok;
    },
    [dynamic, layoutId, persistSets],
  );

  const pruneToAllowedIds = useCallback(
    (allowedIds) => {
      const allowed = new Set(allowedIds);
      setSets((prev) => {
        const next = pruneSetAssetIds(prev, allowed, dynamic);
        if (JSON.stringify(prev) !== JSON.stringify(next)) setDirty(true);
        return next;
      });
    },
    [dynamic],
  );

  const activeAssetIds = dynamic
    ? activeSet?.contentMappingIds || []
    : activeSet?.layoutMappingIds || [];

  const canSavePersonalization = useMemo(
    () => personalizationSetsReadyToSave(sets, dynamic),
    [sets, dynamic],
  );

  return {
    sets,
    activeSetId,
    setActiveSetId,
    onSetsChange,
    loading,
    saving,
    dirty,
    discardChanges,
    save,
    toggleAssetForActiveSet,
    activeAssetIds,
    canSelectAssets,
    canSavePersonalization,
    pruneToAllowedIds,
    pruneToAllowedIdsAndSave,
  };
};
